import { useEffect, useRef } from 'react';
import { useUi } from '../store/ui';

/**
 * Atalhos da Secao 6.8, compativeis com o Gmail.
 * A tabela e dado, nao codigo: remapear e trocar `keys` aqui (a tela de
 * configuracao que edita isso vem depois; a estrutura ja aceita).
 */
export interface Shortcut {
  id: string;
  keys: string;
  label: string;
  group: 'Navegação' | 'Ações' | 'Mensagem' | 'Geral';
}

export const SHORTCUTS: Shortcut[] = [
  { id: 'compose', keys: 'c', label: 'Escrever', group: 'Mensagem' },
  { id: 'reply', keys: 'r', label: 'Responder', group: 'Mensagem' },
  { id: 'replyAll', keys: 'a', label: 'Responder a todos', group: 'Mensagem' },
  { id: 'forward', keys: 'f', label: 'Encaminhar', group: 'Mensagem' },
  { id: 'send', keys: 'mod+enter', label: 'Enviar', group: 'Mensagem' },

  { id: 'next', keys: 'j', label: 'Próxima conversa', group: 'Navegação' },
  { id: 'prev', keys: 'k', label: 'Conversa anterior', group: 'Navegação' },
  { id: 'back', keys: 'u', label: 'Voltar à lista', group: 'Navegação' },
  { id: 'search', keys: '/', label: 'Buscar', group: 'Navegação' },
  { id: 'palette', keys: 'mod+k', label: 'Paleta de comandos', group: 'Navegação' },
  { id: 'goInbox', keys: 'g i', label: 'Ir para Entrada', group: 'Navegação' },
  { id: 'goSent', keys: 'g t', label: 'Ir para Enviados', group: 'Navegação' },
  { id: 'goDrafts', keys: 'g d', label: 'Ir para Rascunhos', group: 'Navegação' },

  { id: 'archive', keys: 'e', label: 'Arquivar', group: 'Ações' },
  { id: 'delete', keys: '#', label: 'Excluir', group: 'Ações' },
  { id: 'spam', keys: '!', label: 'Marcar spam', group: 'Ações' },
  { id: 'flag', keys: 's', label: 'Sinalizar', group: 'Ações' },
  { id: 'snooze', keys: 'b', label: 'Adiar', group: 'Ações' },
  { id: 'check', keys: 'x', label: 'Selecionar', group: 'Ações' },
  { id: 'move', keys: 'v', label: 'Mover para', group: 'Ações' },
  { id: 'undo', keys: 'mod+z', label: 'Desfazer última ação', group: 'Ações' },

  { id: 'sync', keys: 'f9', label: 'Sincronizar agora', group: 'Geral' },
  { id: 'help', keys: '?', label: 'Lista de atalhos', group: 'Geral' },
];

export type ShortcutId = (typeof SHORTCUTS)[number]['id'];

/** Atalhos que valem mesmo com o foco dentro de um campo de texto. */
// `undo` fica de fora: dentro de um campo, Ctrl+Z e do texto, nao do aviso.
const SEMPRE_ATIVOS = new Set(['palette', 'send', 'sync']);

/** Tecla de funcao nao digita nada: vale com atalhos desligados, como Ctrl/Cmd. */
const ehTeclaDeFuncao = (key: string) => /^f\d{1,2}$/.test(key);

function eventKey(event: KeyboardEvent): string {
  const mod = event.metaKey || event.ctrlKey ? 'mod+' : '';
  const key = event.key === ' ' ? 'space' : event.key.toLowerCase();
  return `${mod}${key}`;
}

function editavel(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT'
  );
}

/** Prefixo de sequencia, no estilo `g` depois `i` do Gmail. */
const PREFIXOS = new Set(SHORTCUTS.filter((s) => s.keys.includes(' ')).map((s) => s.keys[0]!));
const JANELA_SEQUENCIA_MS = 1200;

export function useShortcuts(handlers: Partial<Record<ShortcutId, () => void>>): void {
  const handlersRef = useRef(handlers);

  // Atualiza fora da renderizacao: o listener so le isto dentro do evento.
  useEffect(() => {
    handlersRef.current = handlers;
  });

  useEffect(() => {
    let pendente: string | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const limparPendente = () => {
      pendente = null;
      if (timer !== undefined) clearTimeout(timer);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.altKey) return;

      const key = eventKey(event);
      const emCampo = editavel(event.target);

      // Atalhos desligados (WYM-06): so os com Ctrl/Cmd continuam, a paleta
      // entre eles. Tecla solta e sequencia `g i` param de existir.
      if (!useUi.getState().atalhosAtivos && !key.startsWith('mod+') && !ehTeclaDeFuncao(key)) {
        limparPendente();
        return;
      }

      // Paleta aberta (flag sincrona do store, antes de o modulo lazy montar):
      // atalho de uma tecla fica suspenso e a letra digitada nesse intervalo
      // vai para o campo da paleta, que le `buscaDaPaleta` ao montar (D3).
      const ui = useUi.getState();
      if (ui.paletteOpen && !key.startsWith('mod+') && !ehTeclaDeFuncao(key)) {
        limparPendente();
        // `useShortcuts` roda em mais de um lugar (AppShell e painel de leitura):
        // quem chega primeiro captura a tecla e os demais a ignoram, senao a
        // letra entra duas vezes no buffer (N3). Dentro do campo o navegador digita.
        if (!emCampo && !event.defaultPrevented && !event.ctrlKey && !event.metaKey) {
          if (event.key.length === 1) {
            event.preventDefault();
            ui.setBuscaDaPaleta(ui.buscaDaPaleta + event.key);
          } else if (event.key === 'Backspace') {
            event.preventDefault();
            ui.setBuscaDaPaleta(ui.buscaDaPaleta.slice(0, -1));
          } else if (event.key === 'Escape') {
            event.preventDefault();
            ui.setPaletteOpen(false);
            ui.setBuscaDaPaleta('');
          }
        }
        return;
      }

      // Sequencia iniciada: a proxima tecla completa ou cancela.
      if (pendente !== null) {
        const combo = `${pendente} ${key}`;
        limparPendente();
        const atalho = SHORTCUTS.find((s) => s.keys === combo);
        const acao = atalho ? handlersRef.current[atalho.id] : undefined;
        if (acao) {
          event.preventDefault();
          acao();
        }
        return;
      }

      if (!emCampo && PREFIXOS.has(key)) {
        pendente = key;
        timer = setTimeout(limparPendente, JANELA_SEQUENCIA_MS);
        return;
      }

      const atalho = SHORTCUTS.find((s) => s.keys === key);
      if (!atalho) return;
      if (emCampo && !SEMPRE_ATIVOS.has(atalho.id)) return;

      const acao = handlersRef.current[atalho.id];
      if (!acao) return;

      event.preventDefault();
      acao();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      limparPendente();
    };
  }, []);
}

const MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);

/** "mod+k" vira "Cmd K" no Mac e "Ctrl K" no resto. */
export function formatKeys(keys: string): string {
  return keys
    .split(' ')
    .map((parte) =>
      parte
        .replace('mod+', MAC ? 'Cmd ' : 'Ctrl ')
        .replace('enter', 'Enter')
        .replace(/^(\w)$/, (c) => c.toUpperCase())
        .replace(/^f(\d{1,2})$/, 'F$1'),
    )
    .join(' depois ');
}
